import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getOrders } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { formatPrice, formatDate } from '@/lib/utils';
import { PageSEO } from '@/components/SEO';

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getOrders()
      .then(res => setOrders(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-8 skeleton rounded w-48 mb-8" />
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 skeleton rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="min-h-screen pt-32 pb-16">
        <PageSEO.orders />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-medium mb-4">No orders yet</h1>
          <p className="text-lg text-stone-600 mb-8">Start shopping to create your first order</p>
          <Link to="/shop" className="btn-primary inline-flex items-center justify-center">
            Shop Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="orders-page">
      <PageSEO.orders />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl md:text-5xl font-medium mb-12">My Orders</h1>

        <div className="space-y-6">
          {orders.map((order) => (
            <Link
              key={order.order_id}
              to={`/orders/${order.order_id}`}
              className="block border border-stone-200 rounded-lg p-6 hover:border-stone-900 transition-colors"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm text-stone-500 mb-1">Order #{order.order_id}</p>
                  <p className="text-sm text-stone-600">{formatDate(order.created_at)}</p>
                </div>
                <Badge className={getStatusColor(order.status)}>
                  {order.status}
                </Badge>
              </div>

              <div className="flex justify-between items-center">
                <p className="text-stone-600">
                  {order.items.length} {order.items.length === 1 ? 'item' : 'items'}
                </p>
                <p className="text-lg font-light">{formatPrice(order.total)}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
